import {Component, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {switchMap} from 'rxjs';
import {Dialog} from '../../../../../interfaces/dialog/dialog';
import {DialogParams} from '../../../../../interfaces/dialog/dialog-params';
import {DialogService} from '../../../../../services/dialog/dialog.service';
import {InvoiceService} from '../../../../../services/accounting/invoice.service';
import {UploadFileService} from '../../../../../services/upload-file/upload-file.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {BillingRequest} from '../../../../../models/accounting/billing-request';
import {Invoice} from '../../../../../models/accounting/invoice';
import {Document} from '../../../../../models/document';
import {UploadFile} from '../../../../../models/upload-file';
import {ParisDatePipe} from '../../../../../pipes/date/paris-date.pipe';
import {SafePipe} from '../../../../../pipes/safe/safe.pipe';

const ACCEPTED_TYPES: string[] = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE: number = 10 * 1024 * 1024;

@Component({
  selector: 'app-affiliate-invoice-file',
  imports: [
    DatePipe,
    NgClass,
    ParisDatePipe,
    SafePipe
  ],
  templateUrl: './affiliate-invoice-file.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './affiliate-invoice-file.component.scss'
})
export class AffiliateInvoiceFileComponent implements Dialog, OnDestroy {

  readonly acceptedTypes: string = ACCEPTED_TYPES.join(',');

  billingRequest!: BillingRequest;
  invoice!: Invoice;
  invoiceFile: Document | null = null;
  storedFileUrl: string | null = null;
  pendingFile: File | null = null;
  pendingFileUrl: string | null = null;
  loadingPreview: boolean = false;
  uploading: boolean = false;
  dragging: boolean = false;
  updated: boolean = false;

  constructor(
    private dialog: DialogService,
    private invoiceService: InvoiceService,
    private uploadFileService: UploadFileService,
    private snackbar: SnackbarService
  ) {
  }

  onDialogInit(params: DialogParams): void {
    this.billingRequest = params.data.billingRequest;
    this.invoice = params.data.billingRequest.invoice;
    this.invoiceFile = this.invoice.invoiceFile;

    if (this.invoiceFile) {
      this.loadStoredFile();
    }
  }

  ngOnDestroy(): void {
    this.revokeUrl(this.storedFileUrl);
    this.revokeUrl(this.pendingFileUrl);
  }

  get previewUrl(): string | null {
    return this.pendingFileUrl ?? this.storedFileUrl;
  }

  get previewFilename(): string {
    return this.pendingFile?.name ?? this.invoiceFile?.filename ?? '';
  }

  isPdfPreview(): boolean {
    return this.pendingFile ? this.pendingFile.type === 'application/pdf' : this.invoiceFile?.isPdf() ?? false;
  }

  isImagePreview(): boolean {
    return this.pendingFile ? this.pendingFile.type.startsWith('image/') : this.invoiceFile?.isImage() ?? false;
  }

  getFileSize(file: File): string {
    if (file.size < 1024 * 1024) {
      return Math.ceil(file.size / 1024) + ' KB';
    }

    return (file.size / (1024 * 1024)).toFixed(1) + ' MB';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    input.value = '';

    if (file) {
      this.selectFile(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragging = !this.uploading;
  }

  onDragLeave(event: DragEvent): void {
    const container = event.currentTarget as HTMLElement;
    if (!container.contains(event.relatedTarget as Node | null)) {
      this.dragging = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragging = false;

    const file = event.dataTransfer?.files.item(0);
    if (file && !this.uploading) {
      this.selectFile(file);
    }
  }

  cancelPendingFile(): void {
    this.revokeUrl(this.pendingFileUrl);
    this.pendingFile = null;
    this.pendingFileUrl = null;
  }

  upload(): void {
    const invoiceId = this.invoice.id;
    if (!this.pendingFile || !invoiceId) {
      return;
    }

    this.uploading = true;

    this.uploadFileService.upload(this.pendingFile).pipe(
      switchMap((uploadFile: UploadFile) => this.invoiceService.updateInvoiceFile(invoiceId, uploadFile)),
      switchMap(() => this.invoiceService.findOneById(invoiceId, {groups: 'file'}))
    ).subscribe({
      next: (invoice: Invoice) => {
        const replaced = this.invoiceFile !== null;

        this.revokeUrl(this.storedFileUrl);
        this.storedFileUrl = this.pendingFileUrl;
        this.invoiceFile = invoice.invoiceFile;
        this.invoice.invoiceFile = invoice.invoiceFile;
        this.pendingFile = null;
        this.pendingFileUrl = null;
        this.uploading = false;
        this.updated = true;

        this.snackbar.success(replaced ? 'The affiliate invoice has been replaced.' : 'The affiliate invoice has been uploaded.');
      },
      error: () => {
        this.uploading = false;
      }
    });
  }

  close(): void {
    this.dialog.closeDialog(this.updated);
  }

  private selectFile(file: File): void {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      this.snackbar.error('The invoice file must be a PDF, JPEG, PNG or WEBP file.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.snackbar.error('The invoice file must not exceed 10 MB.');
      return;
    }

    this.cancelPendingFile();
    this.pendingFile = file;
    this.pendingFileUrl = URL.createObjectURL(file);
  }

  private loadStoredFile(): void {
    const invoiceId = this.invoice.id;
    if (!invoiceId) {
      return;
    }

    this.loadingPreview = true;

    this.invoiceService.getInvoiceFile(invoiceId).subscribe({
      next: (blob: Blob) => {
        this.revokeUrl(this.storedFileUrl);
        this.storedFileUrl = URL.createObjectURL(blob);
        this.loadingPreview = false;
      },
      error: () => {
        this.loadingPreview = false;
      }
    });
  }

  private revokeUrl(url: string | null): void {
    if (url) {
      URL.revokeObjectURL(url);
    }
  }
}
